import { useCallback } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { FileWithDirectoryPath } from "lib/hooks/api/customer_documents/types";
import { useUploadCustomerDocuments } from "lib/hooks/api/customer_documents/useUploadCustomerDocuments";
import { CustomerDocumentType } from "lib/types/graphQLEnums";
import UploadCustomerDocumentsForm from "./upload_form/UploadCustomerDocumentsForm";
import { useDocumentUploadProgress } from "./DocumentUploadProgressProvider";

type Props = {
  isModal: boolean;
  uiPath: string | undefined; // Only present if we're already in a folder
  resourceId?: string;
  onClose?: () => void;
};
export default function UploadContentLibrary({ uiPath, isModal, resourceId, onClose }: Props) {
  const { control, watch, reset } = useForm<{ files: FileWithDirectoryPath[] }>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const files = watch("files");

  const { filesProcessing, startProcessing, uploadStatuses, updateFileStatus } =
    useDocumentUploadProgress();

  /**
   * Resets the react form to empty and closes the modal.
   */
  const onFinish = useCallback(() => {
    reset({ files: [] });
    onClose?.();
  }, [reset, onClose]);

  const { mutate: uploadFiles, isLoading: isUploadingFiles } = useUploadCustomerDocuments(
    updateFileStatus,
    CustomerDocumentType.ContentLibrary,
  );

  const isLoading = isUploadingFiles || filesProcessing;

  return (
    <UploadDocumentsContainer aria-label="Upload Content library documents">
      <UploadCustomerDocumentsForm
        name="files"
        control={control}
        isLoading={isLoading}
        includeDescription={isModal}
        maxHeight={150}
      />
      {(isModal || files.length > 0) && (
        <ButtonContainer>
          <Button buttonType="secondary" onClick={onFinish}>
            {isModal ? "Close" : "Cancel"}
          </Button>
          <Button
            disabled={files.length === 0}
            loading={isLoading}
            onClick={async () => {
              startProcessing(files);
              uploadFiles({
                files,
                uploadStatuses,
                uiPath,
                resourceId,
              });
            }}
          >
            Add to library
          </Button>
        </ButtonContainer>
      )}
    </UploadDocumentsContainer>
  );
}

const UploadDocumentsContainer = styled(Flex)({
  gap: 12,
  flexDirection: "column",
  width: 600,
});

const ButtonContainer = styled(Flex)({
  justifyContent: "flex-end",
  gap: 16,
  marginTop: 16,
});
